import * as IoC from 'typescript-ioc';
import { action, observable } from 'mobx';
import { AbstractFormViewModelController } from '../../infrastructure/implementations/form-viewmodel-controller';
import { FormFieldProps, FormVmItemConfigType, InitialData } from '../../infrastructure/form-viewmodel-controller';
import { alertServerError } from '../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../infrastructure/error-parsing';
import { ConnectionTemplateValues } from './conections-templates/connections-template-form/connection-template.mapper';
import { IBlueprintsDAO } from '../../api/blueprints/blueprints.dao';
import { IBlueprintDTO } from '../../api/blueprints/blueprint.types';
import { NONE } from '../../constants/common.constants';
import { convertBoolTypeForMixPanel } from '../../tracking-service/tracking-values';
import { TObject } from '../../infrastructure/type-extensions';
import { ISubWindowsService } from '../../infrastructure/sub-windows';
import TrackingServiceProvider from '../../tracking-service/tracking-service-provider';
import { IGlobalStorage } from '../../infrastructure/global-storage';
import { MeetingTemplateValues } from './meeting-templates/meeting-template-form/meeting-template.mapper';
import { EventBlueprintValues } from './event-blueprints/event-blueprint-form/event-blueprint.mapper';


export type BlueprintValues = ConnectionTemplateValues | MeetingTemplateValues | EventBlueprintValues;

// TODO: move to the root of the blueprints and extends other forms from it;
export class BaseBlueprintFormVM {
    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }
    public static handleInternalException(errorMessage: string, title: string = 'Error'): void {
        if (errorMessage) alertServerError(title, errorMessage);
    }
    protected static createFormVM(configList: FormVmItemConfigType[], initialData: InitialData = {}): AbstractFormViewModelController {
        return new class extends AbstractFormViewModelController {
            constructor() {
                super(initialData, 'ConnectionTemplatesFormVM', configList);
            }
        }();
    }

    @observable
    protected formDataCtrl: AbstractFormViewModelController;

    @observable
    protected loading: boolean = false;

    @observable
    protected blueprintItemSnapshot: BlueprintValues = {} as BlueprintValues;

    constructor(
        protected readonly blueprintId: string = '',
        @IoC.Inject protected readonly blueprintsDAO: IBlueprintsDAO = IoC.Container.get(IBlueprintsDAO),
        @IoC.Inject protected readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject protected readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject protected readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),

    ) { }

    public get formVM(): AbstractFormViewModelController {
        return this.formDataCtrl;
    }

    public get isLoading(): boolean {
        return this.loading;
    }

    public get isEditMode(): boolean {
        return !!this.blueprintId;
    }

    public getFormFieldPropsByKey(key: string): FormFieldProps {
        return {
            value: this.formDataCtrl.getValue(key),
            setValue: value => this.formDataCtrl.setValue(key, value),
            setTouched: () => this.formDataCtrl.setValueTouched(key),
            ...this.formDataCtrl.getValueValidationState(key)
        };
    }

    @action
    public async getBluePrint(accountId: number): Promise<IBlueprintDTO> {
        this.startLoading();

        try {
            return await this.blueprintsDAO.getBlueprint<IBlueprintDTO>({ accountId: accountId, blueprintId: this.blueprintId });
        } catch (error) {
            BaseBlueprintFormVM.handleApiException(error);
        } finally {
            this.stopLoading();
        }
    }

    // For the destructing in view. As a key here - value in FormField enum.
    public getFormFields(keys: TObject<string>, keysToSkip?: string[]): TObject<FormFieldProps> {
        const formFields = {};

        for (const key of Object.values(keys)) {
            if (keysToSkip && keysToSkip.includes(key)) continue;
            formFields[key] = this.getFormFieldPropsByKey(key);
        }

        return formFields;
    }

    @action
    protected initializeFormData = (configList: FormVmItemConfigType[], initialData: InitialData = {}) => {
        this.formDataCtrl = BaseBlueprintFormVM.createFormVM(configList, initialData);
    };

    @action
    protected startLoading(): void {
        this.loading = true;
    }

    @action
    protected stopLoading(): void {
        this.loading = false;
    }

    protected buildEditableFieldCheckMixPanel(field: string): string {
        if (!this.isEditMode) return `${this.formDataCtrl.getValue(field) || NONE}`;

        const currentValue = this.formDataCtrl.getValue(field);
        const snapshotValue = this.blueprintItemSnapshot[field];
        const isChanged = snapshotValue != null
            ? snapshotValue !== currentValue
            : Boolean(snapshotValue) !== Boolean(currentValue);

        return `${convertBoolTypeForMixPanel(isChanged)} - ${currentValue || NONE}`;
    }
}
